// skipcq: JS-W1028, JS-0128
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import Alert from '@mui/material/Alert';

function ReviewRowCheckbox({ rowIndex, beerName, checked, disabled, onToggleRow }) {
  const handleChange = useCallback(() => {
    onToggleRow(rowIndex);
  }, [onToggleRow, rowIndex]);

  return (
    <Checkbox
      checked={checked}
      disabled={disabled}
      onChange={handleChange}
      inputProps={{
        'aria-label': `Select ${beerName}`,
      }}
    />
  );
}

ReviewRowCheckbox.propTypes = {
  rowIndex: PropTypes.number.isRequired,
  beerName: PropTypes.string.isRequired,
  checked: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
  onToggleRow: PropTypes.func.isRequired,
};

/**
 * SheetBeerReviewTable
 * Shows validated rows with selection and error status.
 */
function SheetBeerReviewTable({ validatedRows, selectedIndices, onSelectionChange }) {
  const selectedSet = useMemo(() => new Set(selectedIndices), [selectedIndices]);

  const handleToggle = useCallback(
    (rowIndex) => {
      if (selectedSet.has(rowIndex)) {
        onSelectionChange(selectedIndices.filter((index) => index !== rowIndex));
        return;
      }
      onSelectionChange([...selectedIndices, rowIndex]);
    },
    [onSelectionChange, selectedIndices, selectedSet],
  );

  const invalidCount = validatedRows.filter((row) => !row.isValid).length;

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Valid rows are selected by default. Fix invalid rows in the CSV and re-upload to include them.
      </Typography>
      {invalidCount > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {invalidCount} row(s) have validation errors and are not selected.
        </Alert>
      )}
      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">Import</TableCell>
              <TableCell>Brewery</TableCell>
              <TableCell>State</TableCell>
              <TableCell>Beer</TableCell>
              <TableCell>Style</TableCell>
              <TableCell>ABV</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {validatedRows.map((row) => {
              const isSelected = selectedSet.has(row.rowIndex);
              const statusText = row.isValid
                ? 'Ready'
                : row.errors.join('; ');
              return (
                <TableRow key={row.rowIndex} selected={isSelected}>
                  <TableCell padding="checkbox">
                    <ReviewRowCheckbox
                      rowIndex={row.rowIndex}
                      beerName={row.beerName}
                      checked={isSelected}
                      disabled={!row.isValid}
                      onToggleRow={handleToggle}
                    />
                  </TableCell>
                  <TableCell>{row.breweryName}</TableCell>
                  <TableCell>{row.stateCode ?? '—'}</TableCell>
                  <TableCell>{row.beerName}</TableCell>
                  <TableCell>{row.styleLabel}</TableCell>
                  <TableCell>{row.abv ?? '—'}</TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color={row.isValid ? 'success.main' : 'error'}
                    >
                      {statusText}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

SheetBeerReviewTable.propTypes = {
  validatedRows: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedIndices: PropTypes.arrayOf(PropTypes.number).isRequired,
  onSelectionChange: PropTypes.func.isRequired,
};

export default SheetBeerReviewTable;
